import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { isNil, last } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import SimpleButton from 'components/Form/SimpleButton';
import withTranslation from 'hoc/withTranslation';
import { clearAll } from 'utils/apiFilesUploadHelpers';
import {
  cancelSolution,
  clearMapData,
  deleteSolution,
  getRoutingTourInfo,
  onSetShowSolution,
  selectTourById,
  selectUnassigned,
  setSolutionJson,
} from '../../../actions';
import trashCan from '../../../global/img/trash.svg';
import cancelIcon from '../../../global/img/cancel.svg';
import plusIcon from '../../../global/img/plus.svg';
import minusIcon from '../../../global/img/minus.svg';

import { tourToRoutingRequest } from '../../../utils/RoutingConverter';
import { getLastValidIteration, getToursDataFromIteration } from '../../../utils/SolutionHelpers';
import TourStatistics from './TourStatistics';

export const RequestStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'inProgress',
  SUCCESS: 'success',
  CANCELED: 'canceled',
  CANCELING: 'canceling...',
  FAILURE: 'failure',
};

const StatusColors = {
  YELLOW: '#FCE83A',
  GREEN: '#56F000',
  RED: '#FF3838',
  ORANGE: '#FFA500',
};

const FileColors = {
  CSV: 'rgba(146, 161, 207, 0.2)',
  JSON: 'rgba(135, 206, 250, 0.2)',
  OTHER: 'rgba(0, 0, 0, 0.1)',
};

// TODO adjust for mobile
export const StyledTour = styled.div(({ isSelected, noHover, fileType, disabled }) => ({
  opacity: disabled && '50%',
  backgroundColor: isSelected
    ? 'rgba(0, 0, 0, 0.06)'
    : fileType === 'csv'
    ? FileColors.CSV
    : fileType === 'json'
    ? FileColors.JSON
    : FileColors.OTHER,
  padding: '0.5rem 0.5rem',
  fontWeight: isSelected && 420,
  display: 'flex',
  alignItems: 'center',
  cursor: (noHover || disabled) ?? 'pointer',
  width: '22rem',
  gap: '0.5rem',
  borderRadius: '0.25rem',
  '&:hover': {
    backgroundColor: noHover ?? 'rgba(0, 0, 0, 0.08)',
  },
}));

const StyledTourName = styled.div`
  display: flex;
  flex: 1 0 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
`;

const StyledTourStatus = styled.div(({ color, disabled }) => ({
  display: 'flex',
  flex: '0 0 auto',
  backgroundColor: color,
  padding: '0.25rem',
  borderRadius: '2rem',
  minWidth: '5rem',
  justifyContent: 'center',
  color: 'white',
  cursor: !disabled && 'pointer',
}));

function getStatusColor(status) {
  switch (status) {
    case RequestStatus.PENDING:
    case RequestStatus.IN_PROGRESS:
      return StatusColors.YELLOW;
    case RequestStatus.SUCCESS:
      return StatusColors.GREEN;
    case RequestStatus.FAILURE:
    case RequestStatus.CANCELED:
      return StatusColors.RED;
    case RequestStatus.CANCELING:
      return StatusColors.ORANGE;
    default:
      return '';
  }
}

const Tour = ({
  request,
  isSelected,
  index,
  apiKey,
  tpaVersion,
  oAuth,
  isRememberLastState,
  isProdEnv,
  hasOngoingRequests,
  translations: {
    solutionsStatusPanel: { deleteSolutionTitle, cancelRequestTitle, disabledSolution },
  },
}) => {
  const dispatch = useDispatch();
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const orders = useSelector((state) => state.orders[solution.show]);
  const user = useSelector(({ user: stateUser }) => stateUser);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[index],
  );
  const currentStep = useSelector(
    ({ usageContext: stateUsageContext }) => stateUsageContext,
  ).currentStep;
  const isProcessing =
    last(request.iterations).isProcessing || last(request.iterations).isProcessing === undefined;
  const hasError = request.error;
  const disabled =
    ((hasOngoingRequests || isProcessing || hasError) && !solution.jsonPending) || solution.json;
  const [updateMap, setUpdateMap] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const showIcon = user.mapSettings.showIntersections || user.mapSettings.showOverlaps;
  const hideRoutes = user.mapSettings.hideAllRoutes;

  useEffect(() => {
    if (!showIcon) setShowStatistics(false);
  }, [showIcon]);

  const handleSelectUnassigned = useCallback(() => dispatch(selectUnassigned(true)), [dispatch]);

  const handleSetShowSolution = useCallback(
    (show) => dispatch(onSetShowSolution(show)),
    [dispatch],
  );
  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );
  const handleCancelSolution = useCallback(
    (evt) => {
      evt.stopPropagation();
      evt.preventDefault();
      const useApiKey = isProdEnv && apiKey && apiKey !== '' && !user.isLastOrdersAddedDemo;
      dispatch(cancelSolution(tpaVersion, useApiKey && apiKey, oAuth, request, index, solution));
    },
    [
      dispatch,
      request,
      apiKey,
      index,
      isProdEnv,
      oAuth,
      solution,
      tpaVersion,
      user.isLastOrdersAddedDemo,
    ],
  );
  const handleDeleteSolution = useCallback((idx) => dispatch(deleteSolution(idx)), [dispatch]);
  const handleSelectTour = useCallback(
    () => dispatch(selectTourById({ index: 0, noMapMovement: false })),
    [dispatch],
  );
  const tempOrders = solution.requests[solution.show].tempOrders;
  const handleGetRoutingTourInfo = useCallback(
    (tour) =>
      dispatch(
        getRoutingTourInfo({
          oAuth,
          routingRequest: tourToRoutingRequest(
            tour,
            tourPlanner,
            tempOrders || orders,
            null,
            hideRoutes,
          ),
          tourId: 0,
          solutionId: index,
        }),
      ),
    [dispatch, oAuth, tourPlanner, index, orders, tempOrders, hideRoutes],
  );

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();

      const toEmptyLast = solution.show === index && solution.requests.length === index + 1;
      handleDeleteSolution({ index: [index], current: solution.show, toEmptyLast });

      if (solution.requests.length === 1) clearAll(dispatch, true, false, solution.show);
      setUpdateMap(true);
    },
    [solution, handleDeleteSolution, index, dispatch, currentStep],
  );

  const onShowClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setShowStatistics(!showStatistics);
  };

  const handleOnTourClick = useCallback(() => {
    if (disabled) return;
    if (isProcessing) {
      handleSetSolutionJson({ jsonUserChange: 'draft' });
    }
    if (!request.status || request.status === RequestStatus.SUCCESS) {
      handleSetShowSolution(index);
      handleSelectTour();

      const filtered = getLastValidIteration(solution.requests[index]);
      const tours = getToursDataFromIteration(filtered).tours;
      if (tours.length === 0) handleSelectUnassigned();
      else handleGetRoutingTourInfo(tours[0]);
    }
  }, [
    request,
    handleSetShowSolution,
    handleSelectTour,
    handleGetRoutingTourInfo,
    index,
    solution,
    isProcessing,
    handleSetSolutionJson,
    handleSelectUnassigned,
    disabled,
  ]);

  const canDeleteSolution =
    isRememberLastState &&
    (request.status === RequestStatus.SUCCESS ||
      request.status === RequestStatus.CANCELED ||
      isNil(request.status));
  const canCancelSolution =
    [RequestStatus.PENDING, RequestStatus.IN_PROGRESS].includes(request.status) &&
    !isNil(request.status);

  useEffect(() => {
    if (updateMap) {
      const filtered = getLastValidIteration(solution.requests[index - 1]);

      if (filtered) {
        const tours = getToursDataFromIteration(filtered).tours;
        handleSelectTour();
        handleGetRoutingTourInfo(tours[0]);
      } else {
        dispatch(clearMapData());
        if ([1, 2].includes(currentStep)) handleSetSolutionJson({ jsonTriggerVRP: true });
      }
      setUpdateMap(false);
    }
  }, [
    handleGetRoutingTourInfo,
    handleSelectTour,
    index,
    solution.requests,
    updateMap,
    dispatch,
    handleSetSolutionJson,
    currentStep,
  ]);

  return (
    <>
      <StyledTour
        isSelected={isSelected}
        onClick={disabled ? undefined : handleOnTourClick}
        disabled={disabled}
        title={disabled ? disabledSolution : ''}
        fileType={request.name?.split('.').pop()}
      >
        <StyledTourName
          data-test-id="problemFileName"
          title={disabled ? disabledSolution : request.name}
        >
          {request.name}
        </StyledTourName>
        {request.status && (
          <StyledTourStatus color={getStatusColor(request.status)} disabled={hasOngoingRequests}>
            {request.status}
          </StyledTourStatus>
        )}
        <StyledWrapper>
          {canCancelSolution && (
            <SimpleButton
              dataTestId="cancelAsyncRequest"
              title={cancelRequestTitle}
              onClick={handleCancelSolution}
              icon={cancelIcon}
              smaller
            />
          )}
          {canDeleteSolution && (
            <SimpleButton
              type="button"
              title={deleteSolutionTitle}
              onClick={handleDelete}
              icon={trashCan}
              smaller
            />
          )}
          {showIcon && (
            <SimpleButton
              type="button"
              title={deleteSolutionTitle}
              onClick={onShowClick}
              icon={!showStatistics ? plusIcon : minusIcon}
              smaller
            />
          )}
        </StyledWrapper>
      </StyledTour>
      {showStatistics && (user.mapSettings?.showIntersections || user.mapSettings?.showOverlaps) && (
        <StyledTour>
          <TourStatistics user={user} />
        </StyledTour>
      )}
    </>
  );
};

export default withTranslation(Tour);
